import { Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider, DateTimePickerProps } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

interface CustomDateTimePickerProps {
  label: string;
  value: Date | string | Dayjs | null;
  onChange: (value: string | null) => void;
  isDisabled?: boolean;
  minDateTime?: string;
  maxDateTime?: string;
  slotProps?: DateTimePickerProps<Dayjs>['slotProps'];
  placement?: 'bottom' | 'top' | 'left' | 'right' | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  maxPopperHeight?: string;
  minutesStep?: number;
  is12HourFormat?: boolean;
  isMandatory?: boolean;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  label,
  value,
  onChange,
  isDisabled = false,
  minDateTime,
  maxDateTime,
  slotProps,
  placement = 'bottom',
  maxPopperHeight,
  minutesStep = 1,
  is12HourFormat = false,
  isMandatory = false,
}) => {
  const [isInvalid, setIsInvalid] = useState(false);

  const timeFormat = is12HourFormat ? 'hh:mm A' : 'HH:mm';
  const dateTimeFormat = `DD/MM/YYYY ${timeFormat}`;

  const parseInputValue = () => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value as Dayjs;
    if (value instanceof Date) return dayjs(value);
    if (typeof value === 'string') {
      let parsed = dayjs(value, dateTimeFormat);
      if (!parsed.isValid()) parsed = dayjs(value);
      return parsed;
    }
    return null;
  };

  const minLimit =
    minDateTime && dayjs(minDateTime, dateTimeFormat).isValid() ? dayjs(minDateTime, dateTimeFormat) : null;
  const maxLimit =
    maxDateTime && dayjs(maxDateTime, dateTimeFormat).isValid() ? dayjs(maxDateTime, dateTimeFormat) : null;

  const maxHeight = maxPopperHeight ? { maxHeight: maxPopperHeight } : {};

  return (
    <div className="flex flex-col w-full">
      <Typography variant="body2" fontWeight={600} color="textPrimary">
        {label}
        {isMandatory && <span style={{ color: 'red' }}>*</span>}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          value={parseInputValue()}
          onChange={(newValue) => {
            if (!newValue) {
              setIsInvalid(false);
              onChange(null);
              return;
            }
            const parsed = dayjs(newValue);
            let valid = parsed.isValid();
            if (valid && minLimit) {
              valid = valid && parsed.isAfter(minLimit.subtract(1, 'minute'));
            }
            if (valid && maxLimit) {
              valid = valid && parsed.isBefore(maxLimit.add(1, 'minute'));
            }
            if (valid) {
              setIsInvalid(false);
              onChange(parsed.format(dateTimeFormat));
            } else {
              setIsInvalid(true);
              onChange(null);
            }
          }}
          format={dateTimeFormat}
          disabled={isDisabled}
          minDateTime={minLimit ?? undefined}
          maxDateTime={maxLimit ?? undefined}
          minutesStep={minutesStep}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              error: isInvalid,
              helperText: isInvalid
                ? `DateTime must be${minLimit ? ` after ${minLimit.format(dateTimeFormat)}` : ''}${
                    minLimit && maxLimit ? ' and' : ''
                  }${maxLimit ? ` before ${maxLimit.format(dateTimeFormat)}` : ''}`
                : '',
            },
            popper: {
              placement: placement,
              sx: {
                ...maxHeight,
                overflowY: 'auto',
                boxShadow: (theme: any) => theme.shadows[8],
              },
            },
            ...slotProps,
          }}
          sx={(theme) => ({
            width: '100%',
            marginTop: '8px',
            '& .MuiOutlinedInput-root': {
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 400,
              borderRadius: '8px',
              borderWidth: '1px',
              borderColor: theme.palette.action.selected,
            },
            '& .MuiInputBase-input': {
              padding: '0px',
              borderWidth: '0px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.primary.main,
            },
          })}
          className={`mt-2 w-full ${isDisabled ? 'cursor-not-allowed' : ''}`}
        />
      </LocalizationProvider>
    </div>
  );
};

export default CustomDateTimePicker;
